import * as React from "react";
import styled from "styled-components";

import { theme } from "../theme/theme";

type Header = {
  accessor: string;
  align: "center" | "left" | "right";
  className?: string;
  text: string | React.ReactChild;
  width?: string;
};

type Item = {
  checked: boolean;
  id: string;
};

type BasicTableProps = {
  data: Item[];
  headers: Header[];
  prepareRow: (data) => any;
};

type RowProps = {
  headers: Header[];
  item: Item;
  prepareRow: (data) => any;
};

const Row = ({ headers, item, prepareRow }: RowProps): React.ReactElement => {
  const row = prepareRow(item);
  return (
    <tr>
      {headers.map((header) => {
        return (
          <td
            className={`${header.align} ${header.className || ""}`}
            data-testid={`row-${row.id}-${header.accessor}`}
            key={`row-${row.id}-${header.accessor}`}
          >
            {row[header.accessor]}
          </td>
        );
      })}
    </tr>
  );
};

const rowCheckedAreEqual = (prevRow, currentRow) => {
  return prevRow.item.checked === currentRow.item.checked;
};

const MemoizedRow = React.memo(Row, rowCheckedAreEqual);

const StyledTable = styled.table`
  color: ${theme.color.black};
  font-family: ${theme.fontFamily};

  thead {
    font-size: ${theme.fontSize.s};
    font-weight: ${theme.fontWeight.bold};
    border: none;
    height: 42px;

    th {
      background-color: ${theme.color.offWhite};
      height: 42px;
      padding: 0px 16px;
      position: sticky;
      position: -webkit-sticky;
      top: 0;
      z-index: 10;
    }
    th.checkboxColumn {
      padding-right: 0px;
    }
    th.statusColumn {
      padding-left: 0px;
      padding-right: 8px;
    }
  }

  tbody {
    font-size: ${theme.fontSize.m};

    td {
      border-bottom: 1px solid ${theme.color.grayLightest};
      border-top: 1px solid ${theme.color.grayLightest};
      height: 27px;
      padding: 16px;
    }
    td.checkboxColumn {
      padding-right: 0px;
    }
    td.statusColumn {
      padding-left: 0px;
      padding-right: 8px;
    }
  }

  .center {
    text-align: center;
  }

  .left {
    text-align: left;
  }

  .right {
    text-align: right;
  }
`;

const BasicTable = ({
  headers,
  data,
  prepareRow,
}: BasicTableProps): React.ReactElement => {
  return (
    <StyledTable data-testid="catalog-table">
      <thead>
        <tr>
          {headers.map((header) => (
            <th
              className={`${header.align} ${header.className || ""}`}
              key={`header-${header.accessor}`}
              style={{ width: header.width || "auto" }}
            >
              {header.text}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((item) => {
          return (
            <MemoizedRow
              headers={headers}
              item={item}
              key={item.id}
              prepareRow={prepareRow}
            />
          );
        })}
      </tbody>
    </StyledTable>
  );
};

export default BasicTable;
